/**
 * Floating action-result toast.
 *
 * Instead of dropping the result of an action into a banner at the top of the
 * page (which forces you to scroll up — painful on long lists and mobile), we
 * float a semi-transparent bar fixed in the viewport, overlaying whatever you
 * were doing. It fades in, lingers a few seconds, then fades out. Tap to
 * dismiss early.
 *
 * `flashToast` is the page-facing entry point: pages already build a
 * `{ kind, message }` flash object per action, so we show it at most once
 * (deduped by object reference) even though renderMain may run many times.
 */

export type ToastKind = 'success' | 'danger' | 'warn';

const shownFlashes = new WeakSet<object>();
let rootEl: HTMLDivElement | null = null;
let stylesInjected = false;

function injectStyles(): void {
  if (stylesInjected) return;
  stylesInjected = true;
  const s = document.createElement('style');
  s.id = 'toast-styles';
  s.textContent = `
    .toast-root {
      position: fixed;
      top: 64px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 1000;
      display: flex;
      flex-direction: column;
      gap: 8px;
      width: min(560px, calc(100vw - 24px));
      pointer-events: none;
    }
    .toast {
      pointer-events: auto;
      cursor: pointer;
      background: rgba(38, 26, 12, 0.86);
      color: #f5ecd6;
      border: 1px solid rgba(212, 169, 60, 0.55);
      border-left: 4px solid var(--c-gold, #d4a93c);
      border-radius: var(--radius, 6px);
      padding: 10px 14px;
      font-family: var(--font-body, serif);
      font-size: 0.95rem;
      line-height: 1.4;
      white-space: pre-line;
      box-shadow: 0 6px 22px rgba(0, 0, 0, 0.4);
      -webkit-backdrop-filter: blur(2px);
      backdrop-filter: blur(2px);
      opacity: 0;
      transform: translateY(-8px);
      transition: opacity 0.28s ease, transform 0.28s ease;
    }
    .toast.toast-in { opacity: 1; transform: translateY(0); }
    .toast.toast-out { opacity: 0; transform: translateY(-8px); }
    .toast-success { border-left-color: var(--c-success, #6f9a4a); }
    .toast-danger  { border-left-color: var(--c-danger, #b23a2c); }
    .toast-warn    { border-left-color: var(--c-gold, #d4a93c); }
    @media (max-width: 600px) { .toast-root { top: 54px; } }
    /* Desktop gets a larger, more prominent bar; mobile keeps the base size. */
    @media (min-width: 601px) {
      .toast-root { top: 72px; width: min(760px, calc(100vw - 48px)); }
      .toast {
        font-size: 1.1rem;
        padding: 15px 22px;
        border-left-width: 5px;
        border-radius: calc(var(--radius, 6px) + 2px);
      }
    }
    @media (prefers-reduced-motion: reduce) {
      .toast { transition: opacity 0.2s ease; transform: none; }
      .toast.toast-in, .toast.toast-out { transform: none; }
    }
  `;
  document.head.appendChild(s);
}

function ensureRoot(): HTMLDivElement {
  if (rootEl && document.body.contains(rootEl)) return rootEl;
  rootEl = document.createElement('div');
  rootEl.className = 'toast-root';
  rootEl.setAttribute('aria-live', 'polite');
  document.body.appendChild(rootEl);
  return rootEl;
}

/** Show a floating toast. Returns immediately; the toast self-removes. */
export function showToast(message: string, kind: ToastKind = 'success', durationMs?: number): void {
  injectStyles();
  const root = ensureRoot();
  const el = document.createElement('div');
  el.className = `toast toast-${kind}`;
  el.setAttribute('role', 'status');
  el.textContent = message;

  let removed = false;
  const remove = (): void => {
    if (removed) return;
    removed = true;
    el.classList.add('toast-out');
    window.setTimeout(() => el.remove(), 320);
  };
  el.addEventListener('click', remove);

  root.appendChild(el);
  // Next frame: trigger the fade-in transition.
  requestAnimationFrame(() => el.classList.add('toast-in'));

  // Danger lingers a touch longer so misses/errors are readable.
  const dur = durationMs ?? (kind === 'danger' ? 6000 : 4200);
  window.setTimeout(remove, dur);
}

/**
 * Show a toast for a page's flash object, at most once. `verify` flashes are
 * left to the page (they need interactive inline buttons) and ignored here.
 */
export function flashToast(flash: { kind: string; message: string } | null | undefined): void {
  if (!flash || flash.kind === 'verify') return;
  if (shownFlashes.has(flash)) return;
  shownFlashes.add(flash);
  const kind: ToastKind =
    flash.kind === 'danger' || flash.kind === 'warn' ? (flash.kind as ToastKind) : 'success';
  showToast(flash.message, kind);
}
